import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Printer } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { livePhotoUrl } from "@/lib/fmUtils";

/**
 * Print-ready view of a GT Selling History selection. Loads records from
 * sessionStorage (populated by the /gt-selling-history page's "Print" button),
 * enriches each row with the current BH available quantity by calling
 * POST /api/gt-selling-history/bh-qty, and renders a compact table sized for
 * A4-landscape printing.
 *
 * Column order matches the exact spec the buyer asked for:
 *   GT SKU · VPN · Description · Chain Retail · GT Price · Last Week ROS ·
 *   Last Week Sales Unit · MD · Store On Hand · BH available Quantity
 */

const fmt2 = (v) => (v === null || v === undefined || v === "" ? "-" : Number(v).toFixed(2));
const fmtPct = (v) => (v === null || v === undefined || v === "" ? "-" : `${(Number(v) * 100).toFixed(1)}%`);
const fmtInt = (v) => (v === null || v === undefined || v === "" ? "-" : Math.round(Number(v)).toLocaleString());

export default function GTSellingHistoryPrint() {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [bhQtyById, setBhQtyById] = useState({});
  const [printed, setPrinted] = useState(false);
  const autoPrintTimer = useRef(null);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("gtHistoryPrintRecords");
      setRecords(raw ? JSON.parse(raw) : []);
    } catch {
      setRecords([]);
    }
  }, []);

  // Fetch BH available quantity for matched records only (unmatched rows have
  // no linked FileMaker product so BH qty is inherently unknown -> show "-").
  useEffect(() => {
    const matched = records.filter((r) => r.matched_record_id);
    if (matched.length === 0) return;
    const ids = Array.from(new Set(matched.map((r) => String(r.matched_record_id))));
    api
      .post("/gt-selling-history/bh-qty", { record_ids: ids })
      .then(({ data }) => setBhQtyById(data || {}))
      .catch(() => setBhQtyById({}));
  }, [records]);

  // Auto-open the browser print dialog once records + BH qty are loaded.
  useEffect(() => {
    if (printed || records.length === 0) return;
    autoPrintTimer.current = setTimeout(() => {
      window.print();
      setPrinted(true);
    }, 700);
    return () => clearTimeout(autoPrintTimer.current);
  }, [records, printed]);

  const enriched = useMemo(
    () =>
      records.map((r) => ({
        ...r,
        _bh_qty: r.matched_record_id ? bhQtyById[String(r.matched_record_id)] : null,
      })),
    [records, bhQtyById]
  );

  if (records.length === 0) {
    return (
      <div className="p-8" data-testid="gt-history-print-empty">
        <Button variant="outline" size="sm" onClick={() => navigate("/gt-selling-history")}>
          <ArrowLeft size={14} className="mr-1.5" /> Back
        </Button>
        <p className="mt-6 text-sm text-zinc-500">No rows selected for printing.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[95vw] bg-white p-4" data-testid="gt-history-print-page">
      <style>{`
        @media print {
          @page { size: A4 landscape; margin: 8mm; }
          .gt-print-hide { display: none !important; }
          table { page-break-inside: auto; }
          tr    { page-break-inside: avoid; page-break-after: auto; }
          thead { display: table-header-group; }
        }
        table.gt-print-table th,
        table.gt-print-table td { padding: 4px 6px; border: 1px solid #d4d4d8; font-size: 11px; }
        table.gt-print-table th { background: #f4f4f5; text-transform: uppercase; letter-spacing: 0.04em; font-size: 10px; color: #52525b; }
        table.gt-print-table td.num { text-align: right; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
        table.gt-print-table td.desc { max-width: 260px; }
      `}</style>

      <div className="gt-print-hide mb-4 flex items-center justify-between">
        <Button
          variant="outline"
          size="sm"
          className="rounded-sm border-zinc-300"
          onClick={() => navigate("/gt-selling-history")}
          data-testid="gt-history-print-back-button"
        >
          <ArrowLeft size={14} className="mr-1.5" /> Back
        </Button>
        <Button
          size="sm"
          className="rounded-sm bg-blue-600 text-white hover:bg-blue-700"
          onClick={() => window.print()}
          data-testid="gt-history-print-now-button"
        >
          <Printer size={14} className="mr-1.5" /> Print
        </Button>
      </div>

      <h1 className="mb-3 text-lg font-bold text-zinc-900">GT Selling History &mdash; {records.length} items</h1>

      <table className="gt-print-table w-full border-collapse" data-testid="gt-history-print-table">
        <thead>
          <tr>
            <th style={{ width: "56px" }}>Photo</th>
            <th>GT SKU</th>
            <th>VPN</th>
            <th>Description</th>
            <th>Chain Retail</th>
            <th>GT Price</th>
            <th>Last Week ROS</th>
            <th>Last Week Sales Unit</th>
            <th>MD</th>
            <th>Store On Hand</th>
            <th>BH available Quantity</th>
          </tr>
        </thead>
        <tbody>
          {enriched.map((r) => (
            <tr key={r.id} data-testid={`gt-history-print-row-${r.id}`}>
              <td>
                {r.matched_record_id ? (
                  <img
                    src={livePhotoUrl("main products", r.matched_record_id, "photo")}
                    alt=""
                    style={{ width: 48, height: 48, objectFit: "contain" }}
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                ) : null}
              </td>
              <td>{r.gt_sku || "-"}</td>
              <td>{r.vendor_part || "-"}</td>
              <td className="desc">{r.description || "-"}</td>
              <td className="num">{fmt2(r.chain_retail)}</td>
              <td className="num">{fmt2(r.gt_price)}</td>
              <td className="num">{fmtPct(r.lw_ros)}</td>
              <td className="num">{fmtInt(r.lw_sales_units)}</td>
              <td className="num">{fmtPct(r.md)}</td>
              <td className="num">{fmtInt(r.store_oh)}</td>
              <td className="num">{r.matched_record_id ? fmtInt(r._bh_qty) : "-"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
